import {
  createOrganizationBillingInfoInputSchema,
  type AresLookupResponseDTO,
} from '@form-factory/schema/contracts';
import { Role } from '@form-factory/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button, toast } from '@utima/ui';
import type { FormState as InformedFormState } from 'informed';
import { ArrowLeft } from 'lucide-react';
import { useMemo, useCallback } from 'react';
import { z } from 'zod';

import { Card } from '@/components/card/Card';
import { BasicForm } from '@/components/form/BasicForm';
import { SchemaFields } from '@/components/form/SchemaFields';
import { FormPage } from '@/components/page/FormPage';
import { AresLookup } from '@/features/billing/components/AresLookup';
import { useBillingFormSchema } from '@/features/billing/hooks/useBillingFormSchema';
import { trpc } from '@/services/trpc';

// Zod schema for search params validation (optional)
const billingSearchSchema = z.object({
  redirect: z.string().optional(),
  tier: z.string().optional(), // Assuming tier is passed as string (e.g., SubscriptionTier enum value)
  interval: z.string().optional(), // Assuming interval is passed as string
});

// Zod schema for frontend validation
const baseValidationSchema =
  createOrganizationBillingInfoInputSchema._def.schema.omit({
    organizationId: true,
  });
type FrontendValidationType = z.infer<typeof baseValidationSchema>;
const frontendFormValidationSchema = baseValidationSchema.refine(
  (data: FrontendValidationType) => {
    if (data.isCompany) {
      return !!data.companyName && data.companyName.trim().length > 0;
    }

    return true;
  },
  {
    message: 'Company name is required when billing to a company.',
    path: ['companyName'],
  },
);
type FrontendBillingFormValues = z.infer<typeof frontendFormValidationSchema>;

export const Route = createFileRoute(
  '/_baseLayout/organizations/$organizationId/billing',
)({
  validateSearch: billingSearchSchema, // Validate search parameters
  component: OrganizationBillingPage,
  beforeLoad: async ({
    context: { accessGuard },
    params: { organizationId },
  }) => accessGuard([Role.SuperAdmin, Role.Admin], organizationId),
  loader: async ({ context: { trpcUtils }, params: { organizationId } }) => {
    const orgData = await trpcUtils.organizations.findOne.ensureData({
      id: organizationId,
    });
    const billInfo = await trpcUtils.billingInfo.getBillingInfo.ensureData({
      organizationId,
    });

    return { organizationData: orgData, initialBillingInfo: billInfo };
  },
});

export function OrganizationBillingPage() {
  const { organizationId } = Route.useParams();
  const searchParams = Route.useSearch(); // Get validated search params
  const { t } = useLingui();
  const navigate = useNavigate();
  const utils = trpc.useUtils();

  const { organizationData, initialBillingInfo } = Route.useLoaderData();

  const { billingFormSchema } = useBillingFormSchema({
    initialIsCompany: initialBillingInfo?.isCompany ?? false,
  });

  const initialFormValues = useMemo((): FrontendBillingFormValues => {
    return {
      isCompany: initialBillingInfo?.isCompany ?? false,
      companyName: initialBillingInfo?.companyName ?? '',
      registrationNumber: initialBillingInfo?.registrationNumber ?? '',
      vatId: initialBillingInfo?.vatId ?? '',
      addressLine1: initialBillingInfo?.addressLine1 ?? '',
      addressLine2: initialBillingInfo?.addressLine2 ?? '',
      city: initialBillingInfo?.city ?? '',
      postalCode: initialBillingInfo?.postalCode ?? '',
      country: initialBillingInfo?.country ?? '',
      billingEmail: initialBillingInfo?.billingEmail ?? null,
    };
  }, [initialBillingInfo]);

  const saveMutation = trpc.billingInfo.createOrUpdateBillingInfo.useMutation({
    onSuccess: async () => {
      toast.success(<Trans>Billing information saved successfully.</Trans>);
      await utils.billingInfo.getBillingInfo.invalidate({ organizationId });
      await utils.organizations.findOne.invalidate({ id: organizationId });

      if (searchParams.redirect) {
        // Try to preserve other query params from the original redirect if any
        // This is a simple approach; a more robust one might involve URLSearchParams
        const redirectUrl = new URL(
          searchParams.redirect,
          window.location.origin,
        );
        if (searchParams.tier)
          redirectUrl.searchParams.set('tier', searchParams.tier);
        if (searchParams.interval)
          redirectUrl.searchParams.set('interval', searchParams.interval);

        navigate({
          to: redirectUrl.pathname + redirectUrl.search,
          // params are usually part of the path, not needed if full path is in redirect
        });
      } else {
        navigate({
          to: '/organizations/$organizationId/edit',
          params: { organizationId },
        });
      }
    },
    onError: error => {
      toast.error(
        <Trans>Failed to save billing information: {error.message}</Trans>,
      );
    },
  });

  const handleAresDataPrefill = useCallback(
    (aresData: AresLookupResponseDTO) => {
      console.log(
        'ARES data selected, prefill handled by AresLookup component:',
        aresData,
      );
    },
    [],
  );

  const handleSubmit = (formState: InformedFormState) => {
    const values = formState.values as FrontendBillingFormValues;
    const validationResult = frontendFormValidationSchema.safeParse(values);
    if (!validationResult.success) {
      const errorMessages = validationResult.error.errors
        .map((e: z.ZodIssue) => `${e.path.join('.')}: ${e.message}`)
        .join('\n');
      toast.error(errorMessages);

      return;
    }

    const dataToSubmit = {
      ...validationResult.data,
      organizationId,
      companyName: validationResult.data.isCompany
        ? validationResult.data.companyName
        : null,
      registrationNumber: validationResult.data.isCompany
        ? validationResult.data.registrationNumber
        : null,
      vatId: validationResult.data.isCompany
        ? validationResult.data.vatId
        : null,
      addressLine2: validationResult.data.addressLine2 || null,
      billingEmail: validationResult.data.billingEmail || null,
    };
    saveMutation.mutate(dataToSubmit);
  };

  const handleCancel = () => {
    if (searchParams.redirect) {
      const redirectUrl = new URL(
        searchParams.redirect,
        window.location.origin,
      );
      navigate({ to: redirectUrl.pathname + redirectUrl.search });
    } else {
      navigate({
        to: '/organizations/$organizationId/edit',
        params: { organizationId },
      });
    }
  };

  const saveButtonText = searchParams.redirect
    ? t`Save and Continue to Subscription`
    : t`Save Billing Information`;

  return (
    <FormPage
      title={`${t`Billing Information`} - ${organizationData?.name || 'Organization'}`}
      breadcrumbs={[
        {
          label: organizationData?.name || t`Organization`,
          to: '/organizations/$organizationId/edit',
          params: { organizationId },
        },
        {
          label: t`Billing Information`,
          to: '/organizations/$organizationId/billing',
          params: { organizationId },
        },
      ]}
    >
      <BasicForm<FrontendBillingFormValues>
        onSubmit={handleSubmit}
        className='space-y-6'
        initialValues={initialFormValues}
      >
        <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
          <div className='md:col-span-1'>
            <Card>
              <AresLookup onLookupSuccess={handleAresDataPrefill} />
            </Card>
          </div>
          <div className='space-y-4 md:col-span-1'>
            <Card>
              <SchemaFields schema={billingFormSchema} />
            </Card>
          </div>
        </div>
        <div className='mt-6 flex justify-between space-x-3'>
          <Button
            data-cta='admin-organization-billing-cancel-button'
            type='button'
            variant='secondary'
            icon={<ArrowLeft size={18} />}
            onClick={handleCancel}
            disabled={saveMutation.isPending}
          >
            <Trans>Cancel</Trans>
          </Button>
          <Button
            data-cta='admin-organization-billing-submit-button'
            type='submit'
            variant='success'
            loading={saveMutation.isPending}
          >
            {saveButtonText}
          </Button>
        </div>
      </BasicForm>
    </FormPage>
  );
}
